import { useQueries } from "@-/com.npmjs.tanstack__react-query";
import { PixelRatio, Platform } from "@-/com.npmjs.react-native";
import { renderGlyphTabIcon } from "@-/dev.improve.haniverse.modules.glyph-tab-icon";
import { useGetGraphemeGlyph } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.glyph/client";
import { toneForPinyin } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.glyph";
import { toDiacriticPinyin } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";
import {
  fontStyleForGrapheme,
  graphemeDisplayText,
  HANIVERSE_COMPOSITE,
} from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.font";
import {
  useActorToneColors,
  useEffectiveHanziScript,
  useEffectiveIsToneColorsEnabled,
} from "@-/dev.improve.haniverse.routes.nav.app._lib.actor.user";
import { useCSSVariable } from "@-/com.npmjs.uniwind";

const CSS_COLOR_PREFIX = /^(#|rgb|hsl)/;

/** A native tab: the readings its icon shows (indexes into `readings`). */
export interface MnemonicTabIconGroup {
  readingIndexes: readonly number[];
}

export function useMnemonicTabGlyphs(
  grapheme: string,
  readings: readonly (string | undefined)[],
  enabled: boolean,
  tabs: readonly MnemonicTabIconGroup[] = readings.map((_, index) => ({
    readingIndexes: [index],
  })),
  maxWidth = 80,
) {
  const getGlyph = useGetGraphemeGlyph();
  const script = useEffectiveHanziScript();
  const toneColors = useActorToneColors();
  const colorsEnabled = useEffectiveIsToneColorsEnabled();
  const foreground = String(useCSSVariable("--color-foreground") ?? "0 0% 9%");
  const defaultColor = CSS_COLOR_PREFIX.test(foreground) ? foreground : "hsl(" + foreground + ")";
  const baseGlyph = getGlyph(grapheme, {
    hanziScript: script,
    uncolored: true,
  });
  const glyphs = readings.map((pinyin) => {
    const tone = pinyin === undefined ? null : toneForPinyin(pinyin);
    return {
      ...baseGlyph,
      color: colorsEnabled && tone !== null ? toneColors[tone] : defaultColor,
    };
  });
  const text = graphemeDisplayText(baseGlyph.display, script);
  const family = fontStyleForGrapheme(baseGlyph.display, script)?.fontFamily ?? HANIVERSE_COMPOSITE;
  // The meaning tab's icon is the character in its font; each reading tab's icon is its pinyin.
  // Native tabs carry no caption, so the icon fills the whole item.
  const icons = readings.map((pinyin) =>
    pinyin === undefined
      ? { family, fontSize: 28, text }
      : {
          family: HANIVERSE_COMPOSITE,
          fontSize: 22,
          text: toDiacriticPinyin(pinyin),
        },
  );
  // Only iOS's native tab bar draws these bitmaps. Web and Android show `icons` as text and use
  // the default `tabs` (one per screen), where the meaning and Details screens both lack pinyin, so
  // their identical `glyph-tab-icon` keys logged "Duplicate Queries found" on every render.
  const isRenderingTabImages = enabled && Platform.OS === "ios";
  const queries = useQueries({
    queries: (isRenderingTabImages ? tabs : []).map(({ readingIndexes }) => {
      const first = icons[readingIndexes[0] ?? 0];
      // A tab holding several readings lists their pinyin, each in its own tone color, between
      // neutral commas.
      const runs = readingIndexes.flatMap((readingIndex, position) => [
        ...(position === 0 ? [] : [{ color: defaultColor, text: ", " }]),
        {
          color: glyphs[readingIndex]?.color ?? defaultColor,
          text: icons[readingIndex]?.text ?? "",
        },
      ]);
      return {
        gcTime: 5 * 60 * 1000,
        queryFn: () =>
          renderGlyphTabIcon(runs, first.family, HANIVERSE_COMPOSITE, first.fontSize, maxWidth),
        queryKey: [
          "glyph-tab-icon",
          runs,
          first.family,
          first.fontSize,
          maxWidth,
          PixelRatio.get(),
        ],
        retry: false,
        staleTime: Infinity,
      };
    }),
  });
  const error = queries.find((query) => query.isError)?.error;
  if (error) {
    throw error;
  }
  return {
    glyphs,
    icons,
    /** One rendered icon per entry of `tabs` on iOS; empty elsewhere. */
    images: queries.map((query) => query.data),
    script,
  };
}
